import { useEffect, useState, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import ContentDetailSheet from "@/components/ContentDetailSheet";
import { Loader2, Plus, FileEdit, Sparkles, AlertTriangle } from "lucide-react";

const CONTENT_TYPE_LABEL = { blog: "Blog", service_page: "Página de servicio", location_page: "Página de ubicación", social_post: "Post social" };
const AUDIENCE_LABEL = { homeowners: "Propietarios", architects: "Arquitectos", commercial: "Comercial", investors: "Inversionistas" };
const STATUS = {
  draft: { label: "Borrador", cls: "bg-[#71717A]/10 text-[#71717A]" },
  pending_review: { label: "En revisión", cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  approved: { label: "Aprobado", cls: "bg-[#0055FF]/10 text-[#0055FF]" },
  scheduled: { label: "Programado", cls: "bg-[#8B5CF6]/10 text-[#8B5CF6]" },
  published: { label: "Publicado", cls: "bg-[#10B981]/10 text-[#10B981]" },
};
const EMPTY_FORM = { content_type: "blog", topic: "", location: "", state: "", zip_code: "", audience: "homeowners" };

export default function ContentPage() {
  const { activeTenantId, activeTenant } = useAuth();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [knowledgeEmpty, setKnowledgeEmpty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [dupWarning, setDupWarning] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [contentRes, sugRes, knowledgeRes] = await Promise.all([
        api.get("/content/items"), api.get("/content/suggestions"), api.get("/knowledge/documents"),
      ]);
      setItems(contentRes.data);
      setSuggestions(sugRes.data);
      setKnowledgeEmpty((knowledgeRes.data || []).length === 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTenantId) load(); }, [activeTenantId, load]);

  useEffect(() => {
    if (location.state?.prefill) {
      openCreate(location.state.prefill);
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = (prefill) => {
    setForm({ ...EMPTY_FORM, ...(prefill || {}) });
    setDupWarning(null);
    setOpen(true);
  };

  const checkDup = async () => {
    if (!form.topic.trim()) return;
    try {
      const res = await api.post("/content/items/check-duplication", { topic: form.topic, content_type: form.content_type });
      setDupWarning(res.data.warning);
    } catch {
      // no bloquea la generación si el chequeo falla
    }
  };

  const generate = async () => {
    if (!form.topic.trim()) return toast.error("Escribí un tema o keyword");
    setGenerating(true);
    try {
      const res = await api.post("/content/items/generate", form);
      toast.success(res.data.error_message ? "Se guardó el borrador, pero la generación falló" : "Contenido generado");
      setOpen(false);
      load();
      setSelected(res.data);
      setSheetOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el contenido");
    } finally {
      setGenerating(false);
    }
  };

  const openDetail = (item) => { setSelected(item); setSheetOpen(true); };

  const onChanged = (updated) => {
    setSelected(updated);
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  return (
    <div className="space-y-6" data-testid="content-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#71717A] max-w-xl">
          Generación de contenido con IA: blog, páginas de servicio/ubicación y posts sociales, con datos
          técnicos siempre respaldados por la base de conocimiento.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-content-button" onClick={() => openCreate()} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">
              <Plus className="h-4 w-4 mr-1" /> Generar contenido
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="content-generate-dialog">
            <DialogHeader><DialogTitle className="font-heading">Generar contenido</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Tipo de contenido</Label>
                <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })}>
                  <SelectTrigger data-testid="content-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CONTENT_TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Tema / keyword</Label>
                <Input data-testid="content-topic-input" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} onBlur={checkDup} placeholder="attic insulation R-value requirements" />
                {dupWarning && (
                  <p className="text-xs text-[#F59E0B] flex items-start gap-1 mt-1" data-testid="content-duplication-inline-warning">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {dupWarning}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Audiencia</Label>
                <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                  <SelectTrigger data-testid="content-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(AUDIENCE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Ciudad</Label>
                  <Input data-testid="content-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Brooklyn" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Estado</Label>
                  <Input data-testid="content-state-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="NY" maxLength={2} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Código postal (opcional, refina la jurisdicción)</Label>
                <Input data-testid="content-zip-input" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} placeholder="11201" />
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="generate-content-button" onClick={generate}
                disabled={generating || activeTenant?.status === "suspended"}
                title={activeTenant?.status === "suspended" ? "Cuenta suspendida" : undefined}
                className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
              >
                {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} Generar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {knowledgeEmpty && (
        <div
          data-testid="knowledge-empty-banner"
          className="flex items-start gap-3 border border-[#F59E0B]/30 bg-[#F59E0B]/10 rounded-sm p-3 text-xs text-[#92400E]"
        >
          <AlertTriangle className="h-4 w-4 text-[#F59E0B] shrink-0 mt-0.5" />
          <span className="flex-1">
            Todavía no cargaste documentos técnicos. Mientras la base esté vacía, el contenido con datos
            técnicos —valores R, códigos de construcción— va a salir marcado "requiere revisión" y no se
            va a poder publicar automáticamente.
          </span>
          <Button asChild size="sm" variant="outline" className="rounded-sm shrink-0 border-[#F59E0B]/40 text-[#92400E] hover:bg-[#F59E0B]/20" data-testid="knowledge-empty-banner-button">
            <Link to="/dashboard/knowledge">Cargar documentos</Link>
          </Button>
        </div>
      )}

      {suggestions.length > 0 && (
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-4" data-testid="content-suggestions-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-[#0055FF]" />
            <h3 className="font-heading text-sm font-bold">Sugerido para este mes</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {suggestions.slice(0, 5).map((s) => (
              <button key={s.keyword_id} data-testid={`content-suggestion-${s.keyword_id}`}
                onClick={() => openCreate({ topic: s.term, content_type: s.suggested_content_type, location: s.location || "", state: s.state || "" })}
                className="text-xs border border-[#E4E4E7] rounded-md px-3 py-1.5 hover:border-[#0055FF] hover:text-[#0055FF] transition-colors">
                {s.term} <span className="text-[#A1A1AA]">· {CONTENT_TYPE_LABEL[s.suggested_content_type]}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card className="rounded-sm border-[#E4E4E7] shadow-none">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center px-6" data-testid="content-empty-state">
            <FileEdit className="h-8 w-8 text-[#A1A1AA]" />
            <p className="mt-3 text-sm font-medium text-[#09090B]">Todavía no generaste contenido</p>
            <p className="mt-1 text-xs text-[#71717A] max-w-sm">
              Acá se generan blogs, páginas de servicio/ubicación y posts sociales con IA, usando los
              datos de tu compañía y tu base de conocimiento.
            </p>
            <Button
              size="sm" className="mt-4 bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
              data-testid="content-empty-state-button" onClick={() => openCreate()}
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Generar el primero
            </Button>
          </div>
        ) : (
          <Table data-testid="content-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Título</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Ubicación</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Revisión</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => {
                const s = STATUS[c.status] || STATUS.draft;
                return (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => openDetail(c)} data-testid={`content-row-${c.id}`}>
                    <TableCell className="font-medium max-w-sm truncate">{c.title}</TableCell>
                    <TableCell className="text-[#71717A]">{CONTENT_TYPE_LABEL[c.content_type]}</TableCell>
                    <TableCell className="text-[#71717A]">{c.location || "-"}</TableCell>
                    <TableCell><Badge className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${s.cls}`}>{s.label}</Badge></TableCell>
                    <TableCell>
                      {c.requires_review && <Badge className="bg-[#EF4444]/10 text-[#EF4444] border-0 text-[10px] rounded-sm">Requiere revisión</Badge>}
                      {c.error_message && <Badge className="bg-[#EF4444]/10 text-[#EF4444] border-0 text-[10px] rounded-sm">Error de generación</Badge>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <ContentDetailSheet item={selected} open={sheetOpen} onOpenChange={setSheetOpen} onChanged={onChanged} />
    </div>
  );
}
